import { action, reaction, observable } from 'mobx';
import i18next from 'i18next';

import { AppManager, Variables } from 'core/index';

import { clientStore } from 'apps/client/app';
import { ProjectConnManager } from 'apps/project/connManager';

import {
  ProfileProjectsQuery,
  InitiativeProjectsQuery,
} from 'apps/project/gql';
import { ProfileSharedWithProjectsQuery } from 'apps/share/gql';

import {
  gutGetAllConnectedProjects,
  isInDesktopMode,
} from 'libs/gutClient/electronBridge';

export default class ProjectsAppManager extends AppManager {
  static registryName = 'ProjectsAppManager';

  @observable projectsConn = null;
  @observable fileSyncedProjectIds = [];

  lastQueryParameter = null;

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.variables = new Variables({
      username: () =>
        clientStore.activeSpace
          ? clientStore.activeSpace?.path
          : clientStore.signedInProfile?.user?.username,
      slug: () => clientStore.activeSpace?.path,
      projectsCursor: () => null,
      projectsSortBy: () => this.projectsConn.sortBy,
      projectsCount: () => this.projectsConn.variables.batchSize,
    });

    this.disposers.push(
      reaction(
        () => ({
          activeSpace: clientStore.activeSpace,
          routeName: clientStore.routerStore.route.name,
        }),
        ({ activeSpace, routeName }) => {
          if (!activeSpace || activeSpace.spaceType === 'PROFILE') {
            // Next "if" is just an optimization so that the first query happens immediately (see { fireImmediately: true })
            // which will use the username from signedInProfile, but if the activeSpace that is resolved is the space
            // for that profile we don't want to requery.
            // There's a boolean so that this skip only happens once, which is reasonable but has no real usecase at time of writing.
            // We could potentially be getting into an activeSpace different than the one in signedInProfile,
            // hence the this.lastQueryParameter check.
            if (
              activeSpace &&
              clientStore.isTheCurrentActiveSpaceTheFirstOneUsed &&
              this.lastQueryParameter === this.variables.username
            ) {
              return;
            }

            const isSharedWithProjects =
              routeName === 'workspace.sharedWithProjects';
            this.setupConnManagerWithQuery(
              isSharedWithProjects
                ? ProfileSharedWithProjectsQuery
                : ProfileProjectsQuery,
            );

            this.lastQueryParameter = this.variables.username;
            this.ProjectsQuery();
          } else if (activeSpace.spaceType === 'INITIATIVE') {
            this.setupConnManagerWithQuery(InitiativeProjectsQuery);

            this.lastQueryParameter = this.variables.slug;
            this.ProjectsQuery();
          }
        },
        { fireImmediately: true },
      ),
    );

    if (isInDesktopMode()) {
      gutGetAllConnectedProjects().then(connectedProjectsResponse => {
        if (connectedProjectsResponse) {
          this.fileSyncedProjectIds = Object.keys(connectedProjectsResponse[0]);
        }
      });
    }
  }

  setupConnManagerWithQuery = Query => {
    this.projectsConn?.dispose();
    this.projectsConn = new ProjectConnManager(this, {
      sortBy: 'recent',
      sortOptions: [
        ['recent', i18next.t('Recent')],
        ['contributions', i18next.t('Contributions')],
        ['followers', i18next.t('Followers')],
        ['pageviews', i18next.t('Views')],
      ],
      filterBy: [],
      containsBy: [],
      startsBy: [],
      initialBatchSize: 12,
      loadMoreQuery: Query,
    });
  };

  @action setProjectsQueryFilter = filterText => {
    if (!this.projectsConn) return;

    if (filterText.length < 2) {
      this.projectsConn.containsBy = [];
    } else {
      this.projectsConn.containsBy = [['Project.name', filterText]];
    }
  };

  @action ProjectsQuery = () => {
    this.projectsConn.loadMore();
  };
}
